/**
 * @fileoverview Match Entity search results for the matching step of the Listings Launch flow.
 *
 * @author Stephanie Yoon (steyoon@yext.com)
 */

import ContentSkeletonCards from '/src/com/yext/listingslaunchstorm/js/components/common/contentskeletoncards';
import {SelectableCardGrid} from '/src/com/yext/listingslaunchstorm/js/components/common/selectablecardgrid';
import {MatchedListing} from '/src/com/yext/listingslaunchstorm/js/data/matchedlisting';
import {MatchInfo} from '/src/com/yext/listingslaunchstorm/js/data/matchinfo';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {DropdownRow, DropdownSummary, DropdownContent} from '/ui/components/dropdownrow/dropdownrow';
import {Input} from '/ui/components/input/input';
import {IconTooltip} from '/ui/components/tooltip/tooltip';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/matchresults.module.scss';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';
import StarSvg from '/src/com/yext/listingslaunchstorm/public/icons/star.svg';

/**
 * Match Entity search results for the matching step of the Listings Launch flow.
 * @param {{
 *  groupId: (?|undefined),
 *  entityId: number,
 *  possibleMatches: Array<MatchInfo>,
 *  loadingPossibleMatches: boolean,
 *  storeCodeMatchedListing: MatchInfo,
 *  createdNewListing: MatchInfo,
 *  showStoreCodeInput: boolean,
 *  storeCodeInvalidated: boolean,
 *  onStoreCodeChange: function(string): ?,
 *  storeCodeInputValue: string,
 *  onSelected: function(MatchedListing): ?,
 *  isSelected: function(MatchedListing): ?,
 *  currentMatches: Set<string>,
 * }} props
 * @return (?|string)
*/
export const MatchResults = ({groupId,
  entityId,
  possibleMatches,
  loadingPossibleMatches,
  storeCodeMatchedListing,
  createdNewListing,
  showStoreCodeInput,
  storeCodeInvalidated,
  onStoreCodeChange,
  storeCodeInputValue,
  onSelected,
  isSelected,
  currentMatches}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isSmallViewport = useIsSmallViewport();

  if (loadingPossibleMatches) {
    return (
      <div className={styles.matchesLoadingDisclaimer}>
        <p>{yext.msg('It may take up to a few seconds for potential matches to load.')}</p>
        <ContentSkeletonCards />
      </div>
    );
  }

  if (groupId === null) {
    return <></>;
  }

  const alternativeMatchOptions = (
    <SelectableCardGrid size={isSmallViewport ? 1 : 3} onSelected={onSelected} isSelected={isSelected}>
      <SelectableCardGrid.Card
        value={storeCodeMatchedListing}
        key="store-code"
        pendoId="listings-launch-flow-matching-search-for-match"
      >
        <ul>
          <li> <div className={styles.cardTitle}>{yext.msg('Search using a Store Code')}</div></li>
          <li>{yext.msg('You can find your Store Code in your Google Business Profile account under Advanced Settings.')}</li>
          <li>
            {showStoreCodeInput
              && (storeCodeInvalidated
                ? (<div className={styles.invalidStoreCodeText}>{yext.msg('This store code does not exist. Please try again.')}</div>)
                : storeCodeMatchedListing.externalId && (<div className={styles.successStoreCodeText}><CheckedSvg /> {yext.msg('Success!')}</div>)
              )
            }
          </li>
          <li className={styles.input}> {showStoreCodeInput
            && (<Input
              id="store-code"
              pendoId="listings-launch-flow-matching-store-id-input"
              label={yext.msg('Store Code')}
              hideLabel={true}
              type="text"
              onChange={onStoreCodeChange}
              placeholder={yext.msg('Search using a Store Code')}
              value={storeCodeInputValue}
              valid={!storeCodeInvalidated}
            />)}
          </li>
        </ul>
      </SelectableCardGrid.Card>
      <SelectableCardGrid.Card
        value={createdNewListing}
        pendoId="listings-launch-flow-matching-create-new-listing"
        key="create-new-listing">
        <ul>
          <li> <div className={styles.cardTitle}>{yext.msg('Create A New Listing')}</div></li>
          <li>{yext.msg('This will automatically create a listing on Google using your current listing details')}</li>
        </ul>
      </SelectableCardGrid.Card>
    </SelectableCardGrid>
  );

  return (
    <div>
      {possibleMatches.length === 0
        && (<p>{yext.msg('There was no match found in your selected Location Group. When we don\'t return matches it\'s because we didn\'t find a location in your Google location group that had a similar name, address, or phone to the entity you are trying to find a listing for on Google. If you know you have a Google Business Profile listing that matches the entity you\'re trying to match, please verify you\'ve chosen the correct location group that it\'s stored in. In addition, if you know the store code for your Google Business Profile listing, you can match to it directly using one of the options below.')}</p>)}
      <SelectableCardGrid size={isSmallViewport ? 1 : 3} onSelected={onSelected} isSelected={isSelected}>
        {possibleMatches.length !== 0
          && <SelectableCardGrid.Header
            title={yext.msg('Potential Matches')}
            description={yext.msg('Select the listing that matches your entity')}
          />}
        {possibleMatches.find(matchInfo => isMatchDisabled(matchInfo, currentMatches))
          && <div className={styles.disabledMatchesCallout}>
            <CalloutBanner type={CalloutBannerType.WARNING}>
              {yext.msg('Any disabled matches are not selectable because they are already matched to an entity.')}
            </CalloutBanner>
          </div>}
        {possibleMatches.map((matchInfo, index) => {
          const matchedListing = new MatchedListing({
            entityId,
            groupId,
            externalId: matchInfo.externalId,
            displayAddress: matchInfo.addressTokens[0],
            isVerified: matchInfo.isVerified,
          });
          const isDisabled = isMatchDisabled(matchInfo, currentMatches);
          const isCurrentMatch = currentMatches.has(matchInfo.externalId);
          const currentMatchText =
            isSmallViewport && isCurrentMatch ? yext.msg(' (Current Match)') : '';
          return (
            <SelectableCardGrid.Card
              value={matchedListing}
              key={matchInfo.externalId}
              pendoId={getPendoIdForMatchResultIndex(index)}
              isDisabled={isDisabled}
            >
              <div className={styles.matchCard}>
                <ul>
                  <li className={styles.cardTitle}>
                    {isDisabled ? matchInfo.name : <span><a href={matchInfo.url} target="_blank" rel="noreferrer">{matchInfo.name}</a>{currentMatchText}</span>}
                  </li>
                  {matchInfo.addressTokens.map((addressToken, tokenIndex) =>
                    <li key={`addressToken-${tokenIndex}`}>{addressToken}</li>,
                  )}
                  <li>{matchInfo.country}</li>
                  <li>{matchInfo.phone}</li>
                  {matchInfo.primaryCategoryName
                    && <li>{yext.msg('Primary Category: {0}', matchInfo.primaryCategoryName)}</li>}
                  {matchInfo.isVerified
                    && <li>
                      <CheckedSvg />
                      {yext.msg('Verified')}
                    </li>
                  }
                </ul>
              </div>
              {currentMatches.has(matchInfo.externalId) && !isSmallViewport && (
                <div className={styles.currentMatchTooltip}>
                  <IconTooltip
                    element={
                      <span>
                        <StarSvg />
                      </span>
                    }
                    tooltipText={yext.msg('Current Match')}
                  />
                </div>
              )}
            </SelectableCardGrid.Card>
          );
        })}
        {isSmallViewport
          ? (<div className={styles.alternativeMatchOptions}>
            <DropdownRow onToggleExpand={() => setIsExpanded(prev => !prev)} isExpanded={isExpanded}>
              <DropdownSummary>
                <div className={styles.dropdownSummary}>{yext.msg('Not seeing the right match?')}</div>
              </DropdownSummary>
              <DropdownContent>
                {alternativeMatchOptions}
              </DropdownContent>
            </DropdownRow></div>)
          : (<>
            <SelectableCardGrid.Header title={yext.msg('Not seeing the right match?')} />
            {alternativeMatchOptions}
          </>)}
      </SelectableCardGrid>
    </div>
  );
};

function getPendoIdForMatchResultIndex(index) {
  switch (index) {
    case 0:
      return 'listings-launch-flow-matching-match-one';
    case 1:
      return 'listings-launch-flow-matching-match-two';
    case 2:
      return 'listings-launch-flow-matching-match-three';
    default:
      return undefined;
  }
}

function isMatchDisabled(matchInfo, currentMatches) {
  return matchInfo.existingSyncEntityId !== undefined && !currentMatches.has(matchInfo.externalId);
}
